import * as React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { getAccessToken } from '../../api/tokenStorage';
import { useTenantApps } from './useTenantApps';
import { AppNotInstalledPage } from './AppNotInstalledPage';

type RequireAppProps = {
    appId: string;
    children: React.ReactNode;
    /** When true (default) shows the "App Not Installed" page. When false, redirects to "/". */
    showPage?: boolean;
};

/**
 * Route guard that blocks rendering if the given app is not installed for the current tenant.
 * Falls through silently when the user is not yet authenticated (react-admin handles that redirect).
 * Shows a spinner while the tenant app list is being loaded from the server.
 */
export function RequireApp({ appId, children, showPage = true }: RequireAppProps) {
    const { isAppInstalled, isLoaded } = useTenantApps();

    // Not authenticated — let react-admin guard the route.
    if (!getAccessToken()) {
        return <>{children}</>;
    }

    // Tenant app list hasn't arrived from the server yet — block rendering until it has.
    if (!isLoaded) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isAppInstalled(appId)) {
        return showPage ? <AppNotInstalledPage appId={appId} /> : <Navigate to="/" replace />;
    }

    return <>{children}</>;
}
